/**
 * Authenticated fetch wrapper. Reads the Clerk session token and attaches it
 * as a Bearer token on every request.
 *
 * We pass `template: 'marginpal-api'` so Clerk issues a token from the
 * backend-specific JWT template (which includes org_id, email, name claims).
 *
 * Usage:
 *   const res = await apiFetch(`${API_URL}/api/Something`);
 *   const data = await res.json();
 */
export async function apiFetch(url, options = {}) {
  if (!window.Clerk?.session) {
    throw new Error('apiFetch called before Clerk session was ready');
  }

  let token = await window.Clerk.session.getToken({ template: 'marginpal-api' });
  if (!token) {
    // getToken() returns null for a beat while Clerk refreshes the session
    // claims (e.g. immediately after org creation). Sending "Bearer null"
    // guarantees a 401, so retry once, then fail like any other request
    // failure — callers and React Query already handle thrown errors.
    await new Promise((resolve) => setTimeout(resolve, 300));
    token = await window.Clerk.session.getToken({ template: 'marginpal-api' });
    if (!token) throw new Error('Clerk session token unavailable');
  }

  // For multipart/form-data (file uploads) the browser MUST set the
  // Content-Type itself so it can include the `boundary=...` parameter.
  // Hard-coding `application/json` would make the server reject the body as
  // unparseable, so skip the default when the caller is sending a FormData.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const res = await fetch(url, { ...options, headers });

  // Global subscription gate: if the API returns 402 (subscription required),
  // redirect to the subscribe page. Defence-in-depth alongside the route guard.
  if (res.status === 402) {
    window.location.href = '/subscribe';
  }

  return res;
}

/**
 * Build an Error for a non-OK response, trying to surface the server's
 * BadRequest-string body as the message (our controllers emit those for
 * validation failures, e.g. `BadRequest("Name is required.")`). The raw
 * status is attached on `err.status` so callers can branch on 404 etc.
 */
export async function httpError(res, fallback) {
  let serverMsg = null;
  try {
    const text = await res.text();
    if (text) {
      // BadRequest("...") serialises to a JSON string literal — unwrap so the
      // toast shows "Name is required." and not `"Name is required."`.
      try { serverMsg = JSON.parse(text); } catch { serverMsg = text; }
      if (typeof serverMsg !== 'string') serverMsg = null;
    }
  } catch { /* body already consumed or unreadable */ }
  const err = new Error(serverMsg || `${fallback}: ${res.status}`);
  err.status = res.status;
  return err;
}
